import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPractices, type PracticeListItem } from '../services/accounts';
import { ACCOUNTS_PRACTICES_KEY } from './queryKeys';

const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_RESULT_LIMIT = 6;

/**
 * Debounced practice name search, shared by the Users list's Practice filter
 * pill and the user drawer's Linked practices typeahead - both need "a
 * handful of practices matching what's typed", not a paginated table.
 */
export function usePracticeSearch(search: string, enabled: boolean) {
    const [debouncedSearch, setDebouncedSearch] = useState(search);

    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [search]);

    const { data, isLoading } = useQuery({
        queryKey: [ACCOUNTS_PRACTICES_KEY, 'search', debouncedSearch],
        queryFn: () =>
            fetchPractices({
                page: 1,
                limit: SEARCH_RESULT_LIMIT,
                q: debouncedSearch || undefined,
                sort: 'name',
                order: 'asc'
            }),
        enabled,
        staleTime: 30_000
    });

    return { results: (data?.data ?? []) as PracticeListItem[], isLoading };
}
